import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireReceiveList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-item/search');
};

export const getTireReceiveById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).equal('id', String(id));

    const payload = fb.build();
    payload.joins = [{ name: 'tire_spec', column_results: ['original_tread_depth', 'pattern'] }];

    const response = await fleetApi.post(`/v2/tire-item/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : dataArr || null;
    return result;
  } catch (error) {
    console.error('Failed to fetch tire receive by ID:', error);
    throw error;
  }
};

export const updateTireReceive = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`v2/tire-item/edit/${id}${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireReceiveList();
    return response;
  } catch (error) {
    console.error('Error updating tire receive:', error);
    throw error;
  }
};

export const useTireTransactionSearch = (serialNumber, page = 0, rowsPerPage = 10, searchField = 'all', searchValue = '') => {
  const payload = useMemo(() => {
    if (!serialNumber) return null;
    const start = page * rowsPerPage;
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['created_at'], false).equal('serial_number', serialNumber);

    if (searchValue) {
      if (searchField === 'all') {
        const built = fb.build();
        built.filters = [
          { name: 'transaction_type', value: searchValue },
          { name: 'license_plate', value: searchValue },
          { name: 'mechanic_name', value: searchValue },
          { name: 'reason', value: searchValue },
          { name: 'condition_before', value: searchValue }
        ];
        return built;
      } else {
        fb.like(searchField, searchValue, 'AND');
      }
    }

    return fb.build();
  }, [serialNumber, page, rowsPerPage, searchField, searchValue]);

  const key = useMemo(() => (payload ? ['/tire-transactions/search', JSON.stringify(payload)] : null), [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const exportTireTransactionSearch = async (serialNumber, searchField = 'all', searchValue = '', totalRecords = 0) => {
  if (!serialNumber) return [];
  try {
    const length = totalRecords > 0 ? totalRecords : 9999;
    const fb = new FilterBuilder().setPaging(0, length).setOrder(['created_at'], false).equal('serial_number', serialNumber);
    let payload = fb.build();

    if (searchValue) {
      if (searchField === 'all') {
        payload.filters = [
          { name: 'transaction_type', value: searchValue },
          { name: 'license_plate', value: searchValue },
          { name: 'mechanic_name', value: searchValue },
          { name: 'reason', value: searchValue },
          { name: 'condition_before', value: searchValue }
        ];
      } else {
        fb.like(searchField, searchValue, 'AND');
        payload = fb.build();
      }
    }

    const response = await fleetApi.post(`/tire-transactions/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(dataArr) ? dataArr : [];
  } catch (error) {
    console.error('Failed to export tire transactions:', error);
    return [];
  }
};
